import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Filter, Pencil, Search, Trash2, X } from 'lucide-react';
import {
  deleteImuProductionInfo,
  getImuProductionList,
  updateImuProductionInfo,
} from '../api/ImuProductionApi';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import styles from './ImuProduction.module.scss';

function ImuProduction() {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [jsonFilters, setJsonFilters] = useState([]);
  const [appliedSearch, setAppliedSearch] = useState('');
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [newFilterKey, setNewFilterKey] = useState('');
  const [newFilterValue, setNewFilterValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [drawer, setDrawer] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editData, setEditData] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editError, setEditError] = useState('');

  const fetchList = useCallback(async (page, serialNumber, filters) => {
    try {
      setLoading(true);
      setError('');
      const response = await getImuProductionList(page, 50, serialNumber, filters);
      setItems(response.data?.items || []);
      setPagination(response.data?.pagination || {});
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Failed to fetch IMU production records.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(currentPage, appliedSearch, appliedFilters);
  }, [appliedFilters, appliedSearch, currentPage, fetchList]);

  const addFilter = () => {
    const key = newFilterKey.trim();
    const value = newFilterValue.trim();
    if (!key || !value) {
      setError('Enter both a data key and value.');
      return;
    }
    if (jsonFilters.some(filter => filter.key === key)) {
      setError(`A filter for ${key} already exists.`);
      return;
    }
    setJsonFilters(current => [...current, { key, value }]);
    setNewFilterKey('');
    setNewFilterValue('');
    setError('');
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setAppliedSearch(searchTerm.trim());
    setAppliedFilters(jsonFilters);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setJsonFilters([]);
    setAppliedSearch('');
    setAppliedFilters([]);
    setCurrentPage(1);
  };

  const openDetails = item => {
    setSelectedItem(item);
    setDrawer('view');
  };

  const openEditor = item => {
    setSelectedItem(item);
    setEditData(JSON.stringify(item.data || {}, null, 2));
    setEditError('');
    setDrawer('edit');
  };

  const closeDrawer = () => {
    if (loading) {
      return;
    }
    setDrawer(null);
    setSelectedItem(null);
    setEditError('');
  };

  const handleSaveEdit = async () => {
    let parsedData;
    try {
      parsedData = JSON.parse(editData);
    } catch (parseError) {
      setEditError(`Invalid JSON: ${parseError.message}`);
      return;
    }
    try {
      setLoading(true);
      setEditError('');
      await updateImuProductionInfo(selectedItem.serial_number, parsedData);
      setSuccess(`Production data updated for ${selectedItem.serial_number}.`);
      setDrawer(null);
      setSelectedItem(null);
      await fetchList(currentPage, appliedSearch, appliedFilters);
    } catch (requestError) {
      setEditError(requestError.response?.data?.detail || 'Failed to update production data.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async serialNumber => {
    try {
      setLoading(true);
      setError('');
      await deleteImuProductionInfo(serialNumber);
      setSuccess(`Production data deleted for ${serialNumber}.`);
      setPendingDelete(null);
      await fetchList(currentPage, appliedSearch, appliedFilters);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Failed to delete production data.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = dateString => (dateString ? new Date(dateString).toLocaleString() : '—');

  const flattenObject = (object, prefix = '') =>
    Object.entries(object || {}).flatMap(([key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? flattenObject(value, fullKey)
        : [{ key: fullKey, value }];
    });

  const renderValue = value => {
    if (value === null || value === undefined) {
      return <span className={styles.nullValue}>null</span>;
    }
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Manufacturing records</span>
          <h1>IMU production</h1>
          <p>
            Search factory records, inspect structured production data, and correct individual
            records.
          </p>
        </div>
      </header>

      {error && (
        <Toast tone="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}
      {success && (
        <Toast tone="success" onClose={() => setSuccess('')}>
          {success}
        </Toast>
      )}

      <section className={styles.filterPanel}>
        <div className={styles.searchRow}>
          <label className={styles.searchField}>
            <Search size={16} />
            <span className={styles.srOnly}>Search serial number</span>
            <input
              type="search"
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && applyFilters()}
              placeholder="Search IMU serial number"
            />
          </label>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={applyFilters}
            disabled={loading}
          >
            Search records
          </button>
          {(appliedSearch || appliedFilters.length > 0) && (
            <button type="button" className={styles.clearButton} onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
        <details className={styles.advancedFilters} open={jsonFilters.length > 0}>
          <summary>
            <Filter size={15} />
            <span>Production-data filters</span>
            {jsonFilters.length > 0 && <b>{jsonFilters.length}</b>}
          </summary>
          <div className={styles.filterBuilder}>
            <input
              value={newFilterKey}
              onChange={event => setNewFilterKey(event.target.value)}
              placeholder="JSON key, e.g. calibration.station"
            />
            <input
              value={newFilterValue}
              onChange={event => setNewFilterValue(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && addFilter()}
              placeholder="Expected value"
            />
            <button type="button" onClick={addFilter}>
              Add filter
            </button>
          </div>
          {jsonFilters.length > 0 && (
            <div className={styles.filterTags}>
              {jsonFilters.map(filter => (
                <span key={filter.key}>
                  <strong>{filter.key}</strong> = {filter.value}
                  <button
                    type="button"
                    onClick={() =>
                      setJsonFilters(current => current.filter(item => item.key !== filter.key))
                    }
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </details>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Production records</h2>
            <p>
              {loading
                ? 'Loading records…'
                : `${items.length} shown · ${pagination.total_count || 0} total`}
            </p>
          </div>
          {(appliedSearch || appliedFilters.length > 0) && (
            <span className={styles.filteredBadge}>Filtered result</span>
          )}
        </div>
        {items.length ? (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>IMU serial</th>
                  <th>Data fields</th>
                  <th>Created</th>
                  <th>Last changed</th>
                  <th>
                    <span className={styles.srOnly}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.serial_number}>
                    <td data-label="IMU serial">
                      <button
                        type="button"
                        className={styles.serialButton}
                        onClick={() => openDetails(item)}
                      >
                        {item.serial_number}
                      </button>
                    </td>
                    <td data-label="Data fields">{flattenObject(item.data).length}</td>
                    <td data-label="Created">{formatDate(item.created_at)}</td>
                    <td data-label="Last changed">{formatDate(item.updated_at)}</td>
                    <td className={styles.actions}>
                      <div className={styles.rowActions}>
                        <button type="button" onClick={() => openDetails(item)}>
                          View
                        </button>
                        <button type="button" onClick={() => openEditor(item)}>
                          <Pencil size={14} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDelete(item.serial_number)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          !loading && (
            <div className={styles.emptyState}>
              <Search size={26} />
              <strong>No production records found</strong>
              <p>Adjust the serial search or production-data filters.</p>
            </div>
          )
        )}
        {pagination.total_pages > 1 && (
          <div className={styles.pagination}>
            <button
              type="button"
              onClick={() => setCurrentPage(page => page - 1)}
              disabled={!pagination.has_prev || loading}
            >
              <ChevronLeft size={15} /> Previous
            </button>
            <span>
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage(page => page + 1)}
              disabled={!pagination.has_next || loading}
            >
              Next <ChevronRight size={15} />
            </button>
          </div>
        )}
      </section>

      <ConfirmDialog
        open={!!pendingDelete}
        title={`Delete production data for ${pendingDelete}?`}
        description="The production record will be permanently removed. This cannot be undone."
        confirmLabel="Delete record"
        busyLabel="Deleting…"
        busy={loading}
        onConfirm={() => handleDelete(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />

      <ActionDrawer
        open={drawer === 'view'}
        eyebrow="Production record"
        title={selectedItem?.serial_number || ''}
        description="Structured values captured during IMU manufacturing."
        onClose={closeDrawer}
        footer={
          <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
            Close
          </button>
        }
      >
        {selectedItem && (
          <div className={styles.detailContent}>
            <dl>
              <div>
                <dt>Created</dt>
                <dd>{formatDate(selectedItem.created_at)}</dd>
              </div>
              <div>
                <dt>Last changed</dt>
                <dd>{formatDate(selectedItem.updated_at)}</dd>
              </div>
            </dl>
            <div className={styles.dataList}>
              {flattenObject(selectedItem.data).map(({ key, value }) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>{renderValue(value)}</dd>
                </div>
              ))}
            </div>
          </div>
        )}
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'edit'}
        eyebrow="Production record"
        title={`Edit ${selectedItem?.serial_number || ''}`}
        description="Edit the complete JSON payload; invalid JSON cannot be saved."
        onClose={closeDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleSaveEdit}
              disabled={loading}
            >
              {loading ? 'Saving…' : 'Save record'}
            </button>
          </>
        }
      >
        <div className={styles.editor}>
          {editError && <div className={styles.formError}>{editError}</div>}
          <textarea
            value={editData}
            onChange={event => setEditData(event.target.value)}
            spellCheck={false}
          />
        </div>
      </ActionDrawer>
    </div>
  );
}

export default ImuProduction;
